import {
  type ComponentPropsWithoutRef,
  memo,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { TooltipWithShortcut } from '@air/component-tooltip-with-shortcut';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { cn } from '@air/utils-tailwind';

import { useCSSVariable } from './useCSSVariable';

export const SIDEBAR_LOCAL_STORAGE_KEY = 'SIDEBAR_SIZE';
const DRAG_TO_CLOSE_THRESHOLD = 150;
const WINDOW_EDGE_PADDING = 16;

export type SidebarResizerProps = Omit<ComponentPropsWithoutRef<'button'>, 'onResize'> & {
  /** CSS variable name to set the width on. Defaults to '--sidebar-width'. */
  cssVariable?: string;
  localStorageKey?: string;
  maxSize?: number;
  /** Minimum space to leave for the rest of the app. Only applies to right panels. */
  minRemainingSpace?: number;
  minSize?: number;
  /** Callback fired when the divider is clicked without dragging, or dragged past the close threshold. When provided, a tooltip with close instructions is shown. */
  onClose?: () => void;
  /** Callback fired when the size changes during resize. */
  onResize?: (width: number) => void;
  /** Callback fired when resizing starts. */
  onResizeStart?: () => void;
  /** Callback fired when resizing ends. */
  onResizeEnd?: () => void;
  /** Which side of the screen the sidebar is on. Affects resizer position and drag direction. */
  side?: 'left' | 'right';
  size: number;
  /** Synchronizes the width from an external source (e.g. sidebar pin/unpin or fullscreen toggle)
   *  so the CSS variable updates before paint without going through the drag flow. */
  externalValue?: number;
  /** When false, the hover resize indicator line is not rendered. Defaults to true. */
  showIndicator?: boolean;
  /** Fired when the user double-clicks to reset to `size`, after the CSS variable is updated. */
  onReset?: () => void;
};

export const SidebarResizer = memo(
  ({
    className,
    cssVariable = '--sidebar-width',
    localStorageKey = SIDEBAR_LOCAL_STORAGE_KEY,
    maxSize = 720,
    minRemainingSpace,
    minSize = 260,
    onClose,
    onResize,
    onResizeStart,
    onResizeEnd,
    side = 'left',
    size,
    externalValue,
    showIndicator = true,
    onReset,
    ...restOfProps
  }: SidebarResizerProps) => {
    const [sidebarWidth, setCSSVariable] = useCSSVariable({
      externalValue,
      initialValue: size,
      localStorageKey,
      name: cssVariable,
    });
    const [canResize, setCanResize] = useState(false);
    const [initialPosition, setInitialPosition] = useState(0);
    const rafIdRef = useRef<number | null>(null);
    const pendingWidthRef = useRef<number | null>(null);
    const pointerIdRef = useRef<number | null>(null);
    const hasDraggedRef = useRef<boolean>(false);

    const handleSizeChange = useCallback(
      (width: number) => {
        setCSSVariable(width);
        onResize?.(width);
      },
      [setCSSVariable, onResize],
    );

    const handleReset = useCallback(() => {
      handleSizeChange(size);
    }, [handleSizeChange, size]);

    const handleDoubleClickReset = useCallback(() => {
      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      pendingWidthRef.current = null;
      setCanResize(false);
      pointerIdRef.current = null;
      hasDraggedRef.current = false;
      handleReset();
      onReset?.();
      onResizeEnd?.();
    }, [handleReset, onReset, onResizeEnd]);

    useEffect(() => {
      return () => {
        if (rafIdRef.current !== null) {
          window.cancelAnimationFrame(rafIdRef.current);
        }
      };
    }, []);

    const sidebarWidthRef = useRef(sidebarWidth);
    sidebarWidthRef.current = sidebarWidth;
    const handleSizeChangeRef = useRef(handleSizeChange);
    handleSizeChangeRef.current = handleSizeChange;

    // Clamp immediately when minSize increases past current width, so the first
    // drag move doesn't snap unexpectedly to the new minimum.
    useIsomorphicLayoutEffect(() => {
      if (sidebarWidthRef.current < minSize) {
        handleSizeChangeRef.current(minSize);
      }
    }, [minSize]);

    const handlePointerDown = useCallback(
      (event: ReactPointerEvent<HTMLButtonElement>) => {
        hasDraggedRef.current = false;
        const currentWidth = parseFloat(document.documentElement.style.getPropertyValue(cssVariable)) || sidebarWidth;

        if (side === 'right') {
          // For right panels, store the initial mouse X + current width
          setInitialPosition(event.clientX + currentWidth);
        } else {
          setInitialPosition(event.clientX - currentWidth);
        }
        pointerIdRef.current = event.pointerId;
        event.currentTarget.setPointerCapture(event.pointerId);
        setCanResize(true);
        onResizeStart?.();
      },
      [cssVariable, onResizeStart, side, sidebarWidth],
    );

    const handlePointerMove = useCallback(
      (event: ReactPointerEvent<HTMLButtonElement>) => {
        if (!canResize || pointerIdRef.current !== event.pointerId) {
          return;
        }

        hasDraggedRef.current = true;

        // Close immediately when dragged past the close threshold
        if (onClose) {
          const distanceFromEdge = side === 'right' ? window.innerWidth - event.clientX : event.clientX;

          if (distanceFromEdge < DRAG_TO_CLOSE_THRESHOLD) {
            pointerIdRef.current = null;
            setCanResize(false);
            onResizeEnd?.();
            onClose();
            return;
          }
        }

        // For right panels, width increases as mouse moves left (subtract clientX)
        // For left panels, width increases as mouse moves right (add clientX)
        const delta = side === 'right' ? initialPosition - event.clientX : event.clientX - initialPosition;

        let effectiveMax = Math.min(maxSize, window.innerWidth - WINDOW_EDGE_PADDING);

        // For right panels, ensure minimum remaining space for the app
        if (side === 'right' && minRemainingSpace) {
          effectiveMax = Math.min(effectiveMax, window.innerWidth - minRemainingSpace);
        }

        let nextWidth = delta;

        if (delta > effectiveMax) {
          nextWidth = effectiveMax;
        } else if (minSize && delta < minSize) {
          nextWidth = minSize;
        }

        pendingWidthRef.current = nextWidth;
        if (rafIdRef.current === null) {
          rafIdRef.current = window.requestAnimationFrame(() => {
            if (pendingWidthRef.current !== null) {
              handleSizeChange(pendingWidthRef.current);
            }
            rafIdRef.current = null;
          });
        }
      },
      [canResize, handleSizeChange, initialPosition, maxSize, minRemainingSpace, minSize, onClose, onResizeEnd, side],
    );

    const handlePointerEnd = useCallback(
      (event: ReactPointerEvent<HTMLButtonElement>) => {
        if (pointerIdRef.current !== event.pointerId) {
          return;
        }

        pointerIdRef.current = null;
        setCanResize(false);

        if (onClose && !hasDraggedRef.current) {
          onClose();
        } else {
          onResizeEnd?.();
        }
      },
      [onClose, onResizeEnd],
    );

    const button = (
      <button
        className={cn(
          'group absolute top-0 bottom-0 z-10 flex h-full w-3 cursor-col-resize select-none flex-col items-center',
          side === 'right' ? 'left-0' : 'right-0',
          className,
        )}
        data-disableselect={true}
        data-draggable={false}
        data-testid="SIDEBAR_RESIZER"
        onDoubleClick={handleDoubleClickReset}
        onPointerCancel={handlePointerEnd}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        type="button"
        {...restOfProps}
      >
        <VisuallyHidden>{onClose ? 'Close the sidebar' : 'Resize the sidebar'}</VisuallyHidden>
        {showIndicator ? (
          <div className="pointer-events-none h-full w-0.5 ads:bg-primary bg-blue-9 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        ) : null}
        {canResize ? <div className="fixed inset-0 z-10 size-full" /> : null}
      </button>
    );

    if (onClose) {
      return (
        <TooltipWithShortcut
          label="Click to close"
          open={canResize ? false : undefined}
          shortcut={['⌘', '\\']}
          side={side === 'right' ? 'left' : 'right'}
        >
          {button}
        </TooltipWithShortcut>
      );
    }

    return button;
  },
);

SidebarResizer.displayName = 'SidebarResizer';
